import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import ReorganizeDrawer from './ReorganizeDrawer';
import SnapshotHistoryPanel from './SnapshotHistoryPanel';
import ExportModal from './inventory/ExportModal';
import { openDownloads, useCaseExports } from './inventory/DownloadsPanel';

/**
 * FileTree — macOS Finder-style folder tree with CRUD operations.
 *
 * Props:
 *   caseId       — UUID of the current case
 *   treeData     — initial folder-tree response { tree, unfiled_count, total_count, skeleton }
 *   currentFilter — currently active folder filter (default '__all__')
 *   onSelect     — callback(filterValue) when user clicks a folder
 *   onTreeChange — callback() when tree is mutated (create/rename/delete)
 */
export default function FileTree({
  caseId,
  treeData: initialTreeData,
  counts = null,   // { total, unfiled, byPath: Map, unit, elsewhere: Map, documents: Map, stays: Map } — per-unit counts
                   // from the inventory; null = the server's
  currentFilter = '__all__',
  onSelect,
  onTreeChange,
}) {
  const { token } = useAuth();
  const [treeData, setTreeData] = useState(initialTreeData);
  const [selected, setSelected] = useState(currentFilter);
  const [status, setStatus] = useState({ text: '', error: false });
  const [contextMenu, setContextMenu] = useState(null);
  const [createTarget, setCreateTarget] = useState(null); // { parentPath }
  const [renameTarget, setRenameTarget] = useState(null); // { path, name }
  const [showReorganize, setShowReorganize] = useState(false);
  const [exportTarget, setExportTarget] = useState(null); // { scope, folderPath }
  const downloads = useCaseExports(caseId);                // how many exports are ready (kept until deleted)
  const statusTimer = useRef(null);

  // Update tree data when prop changes
  useEffect(() => {
    if (initialTreeData) setTreeData(initialTreeData);
  }, [initialTreeData]);

  useEffect(() => {
    setSelected(currentFilter);
  }, [currentFilter]);

  function showStatus(text, error = false) {
    setStatus({ text, error });
    if (statusTimer.current) clearTimeout(statusTimer.current);
    if (!error) {
      statusTimer.current = setTimeout(() => setStatus({ text: '', error: false }), 3000);
    }
  }

  function handleSelect(filterValue) {
    setSelected(filterValue);
    onSelect?.(filterValue);
  }

  // ── API helpers ──────────────────────────────────────────
  const authHeaders = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token && token !== 'dev-mode') h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  async function refreshTree() {
    try {
      const data = await api.get(`/documents/case/${caseId}/folder-tree`);
      setTreeData(data);
      onTreeChange?.();
    } catch (err) {
      showStatus('Refresh failed: ' + err.message, true);
    }
  }

  async function handleCreate(parentPath, name) {
    const fullPath = parentPath ? parentPath.replace(/\/$/, '') + '/' + name : '/' + name;
    setCreateTarget(null);
    try {
      showStatus('Creating folder…');
      await api.post(`/documents/case/${caseId}/folders`, { folder_path: fullPath });
      showStatus('Created ' + fullPath);
      await refreshTree();
    } catch (err) {
      showStatus('Create failed: ' + err.message, true);
    }
  }

  async function handleRename(oldPath, newName) {
    setRenameTarget(null);
    const parentPath = oldPath.substring(0, oldPath.lastIndexOf('/')) || '';
    const newPath = parentPath + '/' + newName;
    try {
      showStatus('Renaming…');
      await api.patch(`/documents/case/${caseId}/rename-folder`, { old_path: oldPath, new_path: newPath });
      showStatus('Renamed → ' + newPath);
      if (selected === oldPath) {
        setSelected(newPath);
        onSelect?.(newPath);
      }
      await refreshTree();
    } catch (err) {
      showStatus('Rename failed: ' + err.message, true);
    }
  }

  async function handleDelete(path) {
    const name = path.split('/').pop();
    if (!window.confirm(`Delete folder "${name}"?\n\nDocuments inside will become unfiled.`)) return;
    setContextMenu(null);
    try {
      showStatus('Deleting…');
      await api.del(`/documents/case/${caseId}/folders`, { folder_path: path });
      showStatus('Deleted ' + path);
      if (selected === path) {
        setSelected('__all__');
        onSelect?.('__all__');
      }
      await refreshTree();
    } catch (err) {
      showStatus('Delete failed: ' + err.message, true);
    }
  }

  // A folder Arc made knows what belongs in it and new documents file themselves there.
  // The attorney can switch that off for one folder; the folder and its documents stay.
  async function handleStopFiling(path) {
    setContextMenu(null);
    try {
      await api.del(`/documents/case/${caseId}/folder-rule`, { folder_path: path });
      showStatus('New documents will no longer file themselves into ' + path);
      await refreshTree();
    } catch (err) {
      showStatus('Could not change the folder: ' + err.message, true);
    }
  }

  const holdsOf = (path) => {
    const find = (list) => {
      for (const n of list || []) {
        if (n.path === path) return n.holds || null;
        const below = find(n.children);
        if (below !== undefined) return below;
      }
      return undefined;
    };
    return find(treeData?.tree) || null;
  };

  // ── Context menu ─────────────────────────────────────────
  function handleContextMenu(e, path) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, path });
  }

  function closeContextMenu() {
    setContextMenu(null);
  }

  // Close on clicks OUTSIDE the menu only. Clicks on a menu item must run
  // that item's own onClick (which closes the menu explicitly). Closing here
  // on inside-clicks races with React 18's synchronous discrete-update flush:
  // this capture-phase native listener fires during the capture phase and
  // unmounts the menu before the item's bubble-phase onClick runs, so the
  // click is silently eaten (that's why "Export folder" did nothing).
  useEffect(() => {
    if (!contextMenu) return;
    const handler = (e) => {
      if (e.target?.closest?.('.ft-ctx-menu')) return;
      closeContextMenu();
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [contextMenu]);

  // ── Render ───────────────────────────────────────────────
  if (!treeData) return null;

  const nodes = treeData.tree || [];
  const unfiledCount = counts ? counts.unfiled : (treeData.unfiled_count || 0);
  const totalCount = counts ? counts.total : (treeData.total_count || 0);

  return (
    <div className="ft-tree">
      {/* Everything in this unit */}
      <TreeRow
        filterValue="__all__"
        icon="📋"
        label={counts?.unit === 'documents' ? 'All documents' : 'All files'}
        count={totalCount}
        depth={0}
        selected={selected === '__all__'}
        onClick={() => handleSelect('__all__')}
      />

      {/* Unfiled */}
      {(unfiledCount > 0 || selected === '__unfiled__') && (
        <TreeRow
          filterValue="__unfiled__"
          icon="📄"
          label="Unfiled"
          count={unfiledCount}
          depth={0}
          selected={selected === '__unfiled__'}
          onClick={() => handleSelect('__unfiled__')}
        />
      )}

      {/* Check these: once the folders say what they hold, the documents Arc could not file by itself */}
      {((counts?.check || 0) > 0 || selected === '__check__') && (
        <TreeRow
          filterValue="__check__"
          icon="❓"
          label="Check these"
          title="Documents Arc could not file by itself — it could not tell what kind they are, or no folder holds that kind yet"
          count={counts?.check || 0}
          depth={0}
          selected={selected === '__check__'}
          onClick={() => handleSelect('__check__')}
        />
      )}

      <div className="ft-divider" />

      {/* New folder button */}
      <div className="ft-row" onClick={() => setCreateTarget({ parentPath: '' })}>
        <span className="ft-caret ft-caret-empty" />
        <span className="ft-icon">＋</span>
        <span className="ft-label" style={{ color: '#1976d2' }}>New folder…</span>
      </div>

      {/* Create input at root level */}
      {createTarget?.parentPath === '' && (
        <CreateInput
          onSubmit={name => handleCreate('', name)}
          onCancel={() => setCreateTarget(null)}
        />
      )}

      {/* Folder nodes */}
      {nodes.map(node => (
        <FolderNode
          counts={counts}
          key={node.path}
          node={node}
          depth={0}
          selected={selected}
          onSelect={handleSelect}
          onContextMenu={handleContextMenu}
          createTarget={createTarget}
          onStartCreate={parentPath => setCreateTarget({ parentPath })}
          onSubmitCreate={handleCreate}
          onCancelCreate={() => setCreateTarget(null)}
          renameTarget={renameTarget}
          onStartRename={(path, name) => setRenameTarget({ path, name })}
          onSubmitRename={handleRename}
          onCancelRename={() => setRenameTarget(null)}
        />
      ))}

      {/* Reorganize with Arc button */}
      <div className="ft-divider" />
      <button
        className="ft-reorg-btn"
        onClick={() => setShowReorganize(true)}
        title="Let Arc propose a folder structure"
      >
        ✦ Reorganize with Arc
      </button>
      <button
        className="ft-reorg-btn"
        onClick={() => setExportTarget({ scope: 'case' })}
        title="Download a zip of every file, organized by folder"
      >
        ⬇️ Export all files
      </button>
      <button
        className="ft-reorg-btn"
        data-testid="open-downloads"
        onClick={openDownloads}
        title="The exports of this case — each stays until someone deletes it"
      >
        📥 Downloads{downloads.ready ? ` (${downloads.ready})` : ''}
      </button>

      {/* Snapshot history */}
      <SnapshotHistoryPanel
        caseId={caseId}
        onRestored={refreshTree}
      />

      {/* Status bar */}
      {status.text && (
        <div className={`ft-status ${status.error ? 'ft-status-error' : ''}`}>
          {status.text}
        </div>
      )}

      {/* Reorganize drawer */}
      {showReorganize && (
        <ReorganizeDrawer
          caseId={caseId}
          currentTree={treeData}
          documentCounts={counts?.documents || null}
          filesThatStay={counts?.stays || null}
          handPlaced={counts?.handPlaced || null}
          copies={counts?.copies || null}
          onClose={() => setShowReorganize(false)}
          onApplied={() => { setShowReorganize(false); refreshTree(); }}
        />
      )}

      {/* Export modal */}
      {exportTarget && (
        <ExportModal
          caseId={caseId}
          scope={exportTarget.scope}
          folderPath={exportTarget.folderPath}
          label={exportTarget.scope === 'case' ? 'all files' : 'folder'}
          onClose={() => setExportTarget(null)}
        />
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="ft-ctx-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div
            className="ft-ctx-item"
            onClick={() => { setCreateTarget({ parentPath: contextMenu.path }); closeContextMenu(); }}
          >
            📁 New Subfolder
          </div>
          <div
            className="ft-ctx-item"
            onClick={() => {
              const name = contextMenu.path.split('/').pop();
              setRenameTarget({ path: contextMenu.path, name });
              closeContextMenu();
            }}
          >
            ✏️ Rename
          </div>
          <div
            className="ft-ctx-item"
            onClick={() => { setExportTarget({ scope: 'folder', folderPath: contextMenu.path }); closeContextMenu(); }}
          >
            ⬇️ Export folder
          </div>
          {holdsOf(contextMenu.path) && (
            <div
              className="ft-ctx-item"
              data-testid="ft-stop-filing"
              title={`Now: ${holdsOf(contextMenu.path)}`}
              onClick={() => handleStopFiling(contextMenu.path)}
            >
              ✦ Stop filing new documents here
            </div>
          )}
          <div className="ft-ctx-sep" />
          <div
            className="ft-ctx-item ft-ctx-danger"
            onClick={() => handleDelete(contextMenu.path)}
          >
            🗑️ Delete
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function TreeRow({ filterValue, icon, label, title, count, depth, selected, hasChildren, onClick, onContextMenu }) {
  return (
    <div
      className={`ft-row ${selected ? 'ft-row-selected' : ''}`}
      style={{ paddingLeft: 6 + depth * 16 }}
      onClick={onClick}
      onContextMenu={onContextMenu}
      data-path={filterValue}
      title={title}
    >
      <span className={`ft-caret ${hasChildren ? 'ft-caret-open' : 'ft-caret-empty'}`}>▶</span>
      <span className="ft-icon">{icon}</span>
      <span className="ft-label">{label}</span>
      {typeof count === 'number' && <span className="ft-count">{count}</span>}
    </div>
  );
}

function FolderNode({
  node, depth, selected, onSelect, onContextMenu, counts = null,
  createTarget, onStartCreate, onSubmitCreate, onCancelCreate,
  renameTarget, onStartRename, onSubmitRename, onCancelRename,
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children?.length > 0;
  const isRenaming = renameTarget?.path === node.path;
  // Empty here but not in the other unit: in Files, a folder that holds only documents
  // cut out of files (and the reverse in Documents). It is not an empty folder — it has
  // nothing to show in this unit. A folder empty in both stays, and so does the open one.
  const holdsHere = counts ? (counts.byPath.get(node.path) || 0) : 1;
  if (holdsHere === 0 && (counts.elsewhere?.get(node.path) || 0) > 0 && selected !== node.path) return null;

  function handleCaretClick(e) {
    e.stopPropagation();
    setExpanded(o => !o);
  }

  return (
    <>
      <div
        className={`ft-row ${selected === node.path ? 'ft-row-selected' : ''}`}
        style={{ paddingLeft: 6 + depth * 16 }}
        onClick={() => onSelect(node.path)}
        onContextMenu={e => onContextMenu(e, node.path)}
        data-path={node.path}
      >
        <span
          className={`ft-caret ${hasChildren ? (expanded ? 'ft-caret-open' : '') : 'ft-caret-empty'}`}
          onClick={hasChildren ? handleCaretClick : undefined}
        >
          ▶
        </span>
        <span className="ft-icon">{hasChildren ? '📂' : '📁'}</span>
        {isRenaming ? (
          <RenameInput
            initialName={renameTarget.name}
            onSubmit={newName => onSubmitRename(node.path, newName)}
            onCancel={onCancelRename}
          />
        ) : (
          <span className="ft-label">{node.name}</span>
        )}
        {!isRenaming && node.holds && (
          <span
            className="ft-holds"
            data-testid="ft-holds"
            title={`New documents of these kinds file themselves here: ${node.holds}`}
          >✦</span>
        )}
        {!isRenaming && typeof (counts ? (counts.byPath.get(node.path) || 0) : node.doc_count) === 'number' && (
          <span className="ft-count">{counts ? (counts.byPath.get(node.path) || 0) : node.doc_count}</span>
        )}
      </div>

      {/* Create input under this folder */}
      {createTarget?.parentPath === node.path && (
        <CreateInput
          depth={depth + 1}
          onSubmit={name => onSubmitCreate(node.path, name)}
          onCancel={onCancelCreate}
        />
      )}

      {/* Children */}
      {hasChildren && expanded && (
        <div className="ft-children">
          {node.children.map(child => (
            <FolderNode
          counts={counts}
              key={child.path}
              node={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              onContextMenu={onContextMenu}
              createTarget={createTarget}
              onStartCreate={onStartCreate}
              onSubmitCreate={onSubmitCreate}
              onCancelCreate={onCancelCreate}
              renameTarget={renameTarget}
              onStartRename={onStartRename}
              onSubmitRename={onSubmitRename}
              onCancelRename={onCancelRename}
            />
          ))}
        </div>
      )}
    </>
  );
}

function CreateInput({ depth = 0, onSubmit, onCancel }) {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      const name = inputRef.current.value.trim();
      if (name) onSubmit(name);
      else onCancel();
    }
    if (e.key === 'Escape') onCancel();
  }

  function handleOk() {
    const name = inputRef.current.value.trim();
    if (name) onSubmit(name);
    else onCancel();
  }

  return (
    <div className="ft-create-row" style={{ paddingLeft: 6 + (depth + 1) * 16 }}>
      <input
        ref={inputRef}
        placeholder="Folder name"
        onKeyDown={handleKeyDown}
      />
      <button className="ft-create-ok" onClick={handleOk}>✓</button>
      <button className="ft-create-cancel" onClick={onCancel}>✕</button>
    </div>
  );
}

function RenameInput({ initialName, onSubmit, onCancel }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Enter') inputRef.current.blur();
    if (e.key === 'Escape') onCancel();
  }

  function handleBlur() {
    const newName = inputRef.current.value.trim();
    if (newName && newName !== initialName) onSubmit(newName);
    else onCancel();
  }

  return (
    <input
      ref={inputRef}
      className="ft-inline-input"
      defaultValue={initialName}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}
